import { useState } from "react";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "layouts/dashboard/DashboardLayout";
import { useDashboardHome } from "hooks/useDashboardHome";
import { ActionCard, StatCard } from "views/dashboard/dashboardHomeShared";
import { TrackRecordSearchInput } from "views/dashboard/TrackRecordSearchInput";

import statTrend from "assets/svg/dashboard-stat-trend.svg";
import statDocument from "assets/svg/dashboard-stat-document.svg";
import statChart from "assets/svg/dashboard-stat-chart.svg";
import actionTrackSearch from "assets/svg/dashboard-action-track-search.svg";
import actionPending from "assets/svg/dashboard-action-pending.svg";
import actionReports from "assets/svg/dashboard-action-reports.svg";
import actionUpdate from "assets/svg/dashboard-action-update.svg";

export const HomeView = (): JSX.Element => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState<string>("");

  const {
    stats,
    statsLoading,
    statsError,
    trackLoading,
    trackError,
    handleTrackRecord,
  } = useDashboardHome();

  const totalApplications = stats?.totalApplications ?? 0;
  const activeApplications = stats?.activeApplications ?? 0;
  const discussion = stats?.discussion ?? 0;
  const filing = stats?.filing ?? 0;
  const statusCount = stats?.status ?? 0;
  const registeredTms = stats?.registeredTms ?? 0;
  const pendingItemsBadge = discussion + filing + statusCount;

  const handleSubmitTrack = async (): Promise<void> => {
    const result = await handleTrackRecord(searchValue);
    if (result !== null) {
      navigate("/dashboard/home/track-results", {
        state: { query: searchValue.trim(), result },
      });
    }
  };

  return (
    <DashboardLayout headerBrand="UpRight" headerTitle="Dashboard">
      <main className="min-h-0 flex-1 overflow-y-auto px-5 pb-6 pt-5 md:px-6">
        <div className="mx-auto min-h-0 w-full max-w-[1404px] rounded-lg bg-white p-6">
          <div className="flex w-full flex-col items-start gap-[22px]">
            <div className="flex w-full shrink-0 flex-col items-start gap-1">
              <h1 className="whitespace-nowrap text-2xl font-bold leading-9 text-[#333]">
                Trademark Management Suite
              </h1>
              <p className="max-w-[341px] text-sm font-normal leading-5 text-[#777]">
                Enterprise-grade trademark processing platform
              </p>
            </div>

            {statsError && (
              <p className="text-xs text-[#fb2c36]">{statsError}</p>
            )}

            <div className="flex w-full flex-col items-stretch gap-5 xl:flex-row">
              <StatCard
                title="Total Applications"
                value={statsLoading ? "—" : totalApplications}
                subtitle="+3 this month"
                icon={statTrend}
                iconAlt="Trend"
              />
              <StatCard
                title="Active Applications"
                value={statsLoading ? "—" : activeApplications}
                subtitle="+3 this month"
                icon={statTrend}
                iconAlt="Trend"
              />
              <StatCard
                title="Discussion"
                value={statsLoading ? "—" : discussion}
                subtitle="Awaiting action"
                icon={statDocument}
                iconAlt="Discussion"
              />
              <StatCard
                title="Filling"
                value={statsLoading ? "—" : filing}
                subtitle="Awaiting action"
                icon={statDocument}
                iconAlt="Filling"
              />
              <StatCard
                title="Registered TMs"
                value={statsLoading ? "—" : registeredTms}
                subtitle="Total portfolio"
                icon={statChart}
                iconAlt="Registered"
              />
            </div>

            <div className="flex w-full flex-col items-stretch gap-5 lg:flex-row">
              <ActionCard
                icon={actionTrackSearch}
                iconAlt="Track status"
                title="Track Status"
                subtitle="Monitor Application progress"
                variant="dark"
              />
              <ActionCard
                icon={actionPending}
                iconAlt="Pending items"
                title="Pending Items"
                subtitle="Review pending actions"
                variant="light"
                badge={statsLoading ? undefined : pendingItemsBadge}
                onClick={() => navigate("/dashboard/home/pending-items")}
              />
              <ActionCard
                icon={actionReports}
                iconAlt="Generate reports"
                title="Generate Reports"
                subtitle="Analytics and insights"
                variant="light"
              />
              <ActionCard
                icon={actionUpdate}
                iconAlt="Update status"
                title="Update Status"
                subtitle="Update Status"
                variant="light"
                onClick={() => navigate("/dashboard/home/update-status")}
              />
            </div>

            <TrackRecordSearchInput
              inputId="track-search-input"
              value={searchValue}
              onChange={setSearchValue}
              onSubmit={() => void handleSubmitTrack()}
            />

            <div>
              <button
                type="button"
                onClick={() => void handleSubmitTrack()}
                disabled={trackLoading || !searchValue.trim()}
                className="inline-flex h-12 min-h-12 w-full items-center justify-center rounded-lg bg-[#222] px-4 text-center text-sm font-medium leading-5 text-white transition-colors hover:bg-[#333] disabled:cursor-not-allowed disabled:opacity-50 sm:w-[251px] sm:px-6 sm:text-base sm:leading-6"
              >
                {trackLoading ? "Searching…" : "Track Record"}
              </button>
            </div>

            {trackError && (
              <p className="text-xs text-[#fb2c36]">{trackError}</p>
            )}
          </div>
        </div>
      </main>
    </DashboardLayout>
  );
};
